import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  ClaimEventHandleResult,
  EventHandle,
  EventHandleConflictResponse,
} from '../models/entities/event-handle.model';
import { EventNotClaimableError } from '../api/dealer/dealer-event-handle.mapper';
import { QueuedEvent } from '../models/entities/events-queue.model';
import { EventInProcessRow } from '../models/entities/events-in-process.model';
import { AgentDataService } from './agent-data.service';
import { AgentDataSyncService } from './agent-data-sync.service';
import { AuthSessionService } from './auth-session.service';
import { BrowserTabService } from './browser-tab.service';
import { EventDataCacheService } from './event-data-cache.service';
import { EventType } from '../models/api-contracts';
import { createAlarmsClipModeReset } from '../models/browser-tab.models';

/**
 * Exclusive event handles — one open event per tab, one holder per event globally.
 * Claim/release race conditions are resolved by the server (mock store simulates DB).
 */
@Injectable({ providedIn: 'root' })
export class EventHandleService {
  private readonly agentData = inject(AgentDataService);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly auth = inject(AuthSessionService);
  private readonly dataSync = inject(AgentDataSyncService);
  private readonly eventCache = inject(EventDataCacheService);

  /** Bumps when holds change so queue / in-process resources can reload. */
  readonly handlesRevision = signal(0);

  /** Event ids with a claim in flight from this window. */
  private readonly openingEventIds = signal<readonly string[]>([]);
  /** Event ids that already failed as unavailable / held — do not claim or toast again. */
  private readonly unopenableEventIds = signal<readonly string[]>([]);
  private readonly claimInFlight = new Map<string, Promise<ClaimEventHandleResult | null>>();

  /**
   * Waiting / parked rows that must not accept another click: locally held,
   * currently opening, or already known unopenable.
   */
  readonly blockedOpenEventIds = computed(() => {
    const ids = new Set<string>([
      ...this.localHeldEventIds(),
      ...this.openingEventIds(),
      ...this.unopenableEventIds(),
    ]);
    return [...ids];
  });

  /** Event ids currently held in this browser window's tabs. */
  localHeldEventIds(): string[] {
    return this.browserTabs
      .tabs()
      .map((tab) => this.browserTabs.getPageState(tab.id).activeEventId)
      .filter((id): id is string => !!id);
  }

  isUnopenable(eventId: string): boolean {
    return this.unopenableEventIds().includes(eventId);
  }

  private isOpening(eventId: string): boolean {
    return this.openingEventIds().includes(eventId);
  }

  /**
   * First caller for this event wins. Later clicks are no-ops until the row is
   * held, marked unopenable, or the attempt is released.
   */
  tryBeginOpen(eventId: string): boolean {
    if (!eventId || this.isUnopenable(eventId) || this.isOpening(eventId)) {
      return false;
    }
    this.markEventOpening(eventId);
    return true;
  }

  /** Drop the in-flight open lock so a later claim of this event can start. */
  releaseOpenAttempt(eventId: string): void {
    this.clearEventOpening(eventId);
  }

  markEventOpening(eventId: string): void {
    this.openingEventIds.update((ids) => (ids.includes(eventId) ? ids : [...ids, eventId]));
  }

  private clearEventOpening(eventId: string): void {
    this.openingEventIds.update((ids) => ids.filter((id) => id !== eventId));
  }

  /** @returns true when this event was not already marked unopenable. */
  markEventUnopenable(eventId: string): boolean {
    if (this.unopenableEventIds().includes(eventId)) {
      return false;
    }
    this.unopenableEventIds.update((ids) => [...ids, eventId]);
    return true;
  }

  activeTabInstanceId(): string | null {
    const tab = this.browserTabs.tabs().find((item) => item.id === this.browserTabs.activeTabId());
    return tab?.instanceId ?? null;
  }

  tabInstanceId(tabId: string): string | null {
    return this.browserTabs.tabs().find((tab) => tab.id === tabId)?.instanceId ?? null;
  }

  /**
   * Claims the next available queue event for a tab (skips events already held).
   * A 409 on one candidate moves on to the next; WORK_LIMIT stops the loop.
   */
  async claimNextForTab(
    tabId: string,
    options?: { excludeEventIds?: string[] },
  ): Promise<ClaimEventHandleResult | null> {
    const tabInstanceId = this.tabInstanceId(tabId);
    const agentEmail = this.auth.email() ?? 'agent@ecam.com';
    const agentDisplayName = this.auth.displayName() ?? (agentEmail.split('@')[0] || agentEmail);
    if (!tabInstanceId) {
      return null;
    }

    const [queue, handles] = await Promise.all([
      firstValueFrom(this.agentData.getEventsQueue()),
      firstValueFrom(this.agentData.getEventHandles()),
    ]);

    const heldIds = new Set(handles.items.map((item) => item.eventId));
    for (const excluded of options?.excludeEventIds ?? []) {
      heldIds.add(excluded);
    }

    // Allow reclaiming the event already on this tab instance (unless excluded).
    const thisTabHandle = handles.items.find((item) => item.tabInstanceId === tabInstanceId);
    if (thisTabHandle && !options?.excludeEventIds?.includes(thisTabHandle.eventId)) {
      heldIds.delete(thisTabHandle.eventId);
    }

    const candidates = queue.items.filter((item) => !heldIds.has(item.id));
    for (const candidate of candidates) {
      const result = await this.claimEvent(candidate, tabInstanceId, agentEmail, agentDisplayName);
      if (result.ok) {
        this.applyHandleToTab(tabId, result.handle, result.handle.siteId ?? candidate.siteId);
        this.bump();
        return result;
      }
      if (result.conflict.code === 'WORK_LIMIT') {
        return result;
      }
    }

    return null;
  }

  /**
   * Claims a specific queue (or parked) event for a tab.
   * Concurrent callers share one in-flight POST; a 409 is a held conflict, not a retry.
   */
  async claimEventForTab(
    tabId: string,
    eventId: string,
    type: EventType = 'process-event',
  ): Promise<ClaimEventHandleResult | null> {
    const inFlight = this.claimInFlight.get(eventId);
    if (inFlight) {
      return inFlight;
    }

    if (this.isUnopenable(eventId)) {
      return this.unopenableConflict(eventId);
    }

    const pending = this.performClaimEventForTab(tabId, eventId, type).finally(() => {
      this.claimInFlight.delete(eventId);
    });
    this.claimInFlight.set(eventId, pending);
    return pending;
  }

  private async performClaimEventForTab(
    tabId: string,
    eventId: string,
    type: EventType,
  ): Promise<ClaimEventHandleResult | null> {
    try {
      const tabInstanceId = this.tabInstanceId(tabId);
      const agentEmail = this.auth.email() ?? 'agent@ecam.com';
      const agentDisplayName = this.auth.displayName() ?? (agentEmail.split('@')[0] || agentEmail);
      if (!tabInstanceId || !eventId) {
        return null;
      }

      const [queue, parked, handles] = await Promise.all([
        firstValueFrom(this.agentData.getEventsQueue()),
        firstValueFrom(this.agentData.getEventsParked()),
        firstValueFrom(this.agentData.getEventHandles()),
      ]);

      const held = handles.items.find((item) => item.eventId === eventId);
      if (held && held.tabInstanceId !== tabInstanceId) {
        return this.heldConflict(eventId, held.agentDisplayName);
      }

      const queued = queue.items.find((item) => item.id === eventId);
      const parkedRow = parked.items.find((item) => item.id === eventId);
      if (!queued && !parkedRow) {
        return this.unopenableConflict(eventId);
      }

      const candidate: QueuedEvent = queued ?? {
        id: parkedRow!.id,
        firstAlarmDetected: parkedRow!.firstAlarmDetected,
        location: parkedRow!.location,
        priority: 0,
        alarmCount: 0,
        type,
      };

      const result = await this.claimEvent(candidate, tabInstanceId, agentEmail, agentDisplayName);
      if (result.ok) {
        this.applyHandleToTab(tabId, result.handle, result.handle.siteId ?? candidate.siteId);
        this.bump();
        return result;
      }

      if (result.conflict.code !== 'WORK_LIMIT') {
        this.markEventUnopenable(eventId);
      }
      return result;
    } catch (error) {
      const conflict = this.readConflict(error);
      if (conflict) {
        if (conflict.code !== 'WORK_LIMIT') {
          this.markEventUnopenable(eventId);
        }
        return { ok: false, conflict };
      }
      return this.unopenableConflict(eventId);
    }
  }

  private unopenableConflict(eventId: string): ClaimEventHandleResult {
    this.markEventUnopenable(eventId);
    return {
      ok: false,
      conflict: {
        code: 'EVENT_HELD',
        eventId,
        heldBy: '',
        message: 'This event is unavailable or already held by another agent.',
      },
    };
  }

  private heldConflict(eventId: string, heldBy: string): ClaimEventHandleResult {
    this.markEventUnopenable(eventId);
    return {
      ok: false,
      conflict: {
        code: 'EVENT_HELD',
        eventId,
        heldBy,
        message: `Event is already held by ${heldBy}`,
      },
    };
  }

  async releaseActiveTabHandle(): Promise<void> {
    const state = this.browserTabs.activePageState();
    this.eventCache.clearEvent(state.activeEventId);
    const released = {
      activeEventId: null,
      activeEventSiteId: null,
      eventHandleId: null,
      claimedAt: null,
      ...createAlarmsClipModeReset(),
    };
    if (!state.eventHandleId) {
      this.browserTabs.updateActivePageState(released);
      return;
    }

    await this.releaseHandle(state.eventHandleId, state.activeEventId ?? undefined);
    this.browserTabs.updateActivePageState(released);
    this.bump();
  }

  async releaseHandleForTabState(eventHandleId: string | null): Promise<void> {
    if (!eventHandleId) {
      return;
    }
    await this.releaseHandle(eventHandleId);
    this.bump();
  }

  /** Releases every hold owned by the signed-in agent (logout). */
  releaseAllForCurrentUser(): void {
    const email = this.auth.email();
    if (!email) {
      return;
    }

    void firstValueFrom(this.agentData.getEventHandles()).then(async (response) => {
      for (const handle of (response?.items ?? []).filter((item) => item.agentEmail === email)) {
        await this.releaseHandle(handle.handleId, handle.eventId);
      }
      this.bump();
    });
  }

  /** Merges static in-process rows with live exclusive handles. */
  mergeInProcessRows(staticRows: EventInProcessRow[], handles: EventHandle[]): EventInProcessRow[] {
    const email = this.auth.email();
    const currentUserId = this.auth.userId() ?? undefined;
    const fromHandles: EventInProcessRow[] = handles.map((handle) => ({
      id: handle.eventId,
      firstAlarmDetected: handle.firstAlarmDetected,
      location: handle.location,
      priority: handle.priority,
      alarmCount: handle.alarmCount,
      agent: handle.agentDisplayName,
      // Handles only carry an email, so ownership is resolvable for the signed-in agent alone.
      userId: email && handle.agentEmail === email ? currentUserId : undefined,
      type: handle.type || 'process-event',
      claimedAt: handle.claimedAt,
    }));

    const handleIds = new Set(fromHandles.map((row) => row.id));
    const retainedStatic = staticRows.filter((row) => !handleIds.has(row.id));
    return [...fromHandles, ...retainedStatic];
  }

  /** Queue items that are not exclusively held by anyone. */
  filterAvailableQueueItems(items: QueuedEvent[], handles: EventHandle[]): QueuedEvent[] {
    const held = new Set(handles.map((item) => item.eventId));
    return items.filter((item) => !held.has(item.id));
  }

  private async claimEvent(
    event: QueuedEvent,
    tabInstanceId: string,
    agentEmail: string,
    agentDisplayName: string,
  ): Promise<ClaimEventHandleResult> {
    try {
      const handle = await firstValueFrom(
        this.agentData.claimEventHandle({
          eventId: event.id,
          tabInstanceId,
          agentEmail,
          agentDisplayName,
          location: event.location,
          firstAlarmDetected: event.firstAlarmDetected,
          priority: event.priority,
          alarmCount: event.alarmCount,
        }),
      );
      return { ok: true, handle };
    } catch (error) {
      if (error instanceof EventNotClaimableError) {
        return {
          ok: false,
          conflict: {
            code: 'EVENT_HELD',
            eventId: event.id,
            heldBy: '',
            message: 'Event is already held',
          },
        };
      }
      const conflict = this.readConflict(error);
      if (conflict) {
        return { ok: false, conflict };
      }
      throw error;
    }
  }

  private applyHandleToTab(tabId: string, handle: EventHandle, siteId?: number): void {
    const openingAnotherEvent =
      this.browserTabs.getPageState(tabId).activeEventId !== handle.eventId;
    const wasActive = this.browserTabs.activeTabId() === tabId;
    if (!wasActive) {
      this.browserTabs.selectTab(tabId);
    }

    this.browserTabs.updateActivePageState({
      activeEventId: handle.eventId,
      activeEventSiteId: Number.isInteger(siteId) && (siteId ?? 0) > 0 ? siteId! : null,
      eventHandleId: handle.handleId,
      claimedAt: handle.claimedAt,
      pendingAction: { type: 'process-event', eventId: handle.eventId },
      // A new event always starts on Alarms. Reclaiming the event already open
      // on this tab keeps Live Cameras or Playback.
      ...(openingAnotherEvent ? { eventViewTab: 'alarms' as const } : {}),
    });
    this.browserTabs.updateTabLabel(tabId, handle.location);
  }

  private async releaseHandle(handleId: string, eventId?: string): Promise<void> {
    try {
      await firstValueFrom(this.agentData.releaseEventHandle({ handleId, eventId }));
    } catch {
      // Best-effort release — server may already have cleared an expired hold.
    }
  }

  private readConflict(error: unknown): EventHandleConflictResponse | null {
    if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
      return null;
    }

    const body = error.error as
      | (Partial<EventHandleConflictResponse> & { detail?: string })
      | string
      | null;
    const record = body && typeof body === 'object' ? body : null;
    const detail =
      (typeof body === 'string' && body.trim()) ||
      (typeof record?.detail === 'string' ? record.detail : '') ||
      record?.message ||
      '';

    if (detail.includes('Open work limit')) {
      return {
        code: 'WORK_LIMIT',
        eventId: record?.eventId ?? '',
        heldBy: '',
        message: detail,
      };
    }

    return {
      code: 'EVENT_HELD',
      eventId: record?.eventId ?? '',
      heldBy: record?.heldBy ?? '',
      message: detail.trim() || 'This event is unavailable or already held by another agent.',
    };
  }

  private bump(): void {
    this.handlesRevision.update((value) => value + 1);
    this.dataSync.notifyEntityChanged('events');
  }
}
